import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Column } from 'primereact/column';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';

const HOST_API = process.env.REACT_APP_URL;

const FORM_EMPTY = {
    estadoFinancieroConceptoId: 0,
    descripcion:                '',
    estadoFinancieroSeccionId:  null,
    esAutomatico:                false,
    codigoAutomatico:            '',
    orden:                       null,
};

const EstadoFinancieroConceptos = () => {
    const toast = useRef(null);
    const { barrio } = useContext(BarrioContext);

    const administracionId = parseInt(localStorage.getItem('administracionId') || '1');

    // ─── Data ────────────────────────────────────────────────────────────────────
    const [conceptos, setConceptos] = useState([]);
    const [secciones, setSecciones] = useState([]);
    const [loading,   setLoading]   = useState(false);

    // ─── Modal alta ──────────────────────────────────────────────────────────────
    const [modalVisible, setModalVisible] = useState(false);
    const [form,         setForm]         = useState({ ...FORM_EMPTY });
    const [submitting,   setSubmitting]   = useState(false);

    // ─── Carga secciones (combo) ───────────────────────────────────────────────────
    useEffect(() => {
        axios.get(`${HOST_API}api/estadoFinancieroConceptos/GetSecciones`)
            .then(({ data }) => setSecciones(data))
            .catch(() => {});
    }, []);

    // ─── Carga conceptos ─────────────────────────────────────────────────────────
    const fetchConceptos = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${HOST_API}api/estadoFinancieroConceptos/GetConceptos?barrioId=${barrio.id}&administracionId=${administracionId}`
            );
            setConceptos(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los conceptos', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, administracionId]);

    useEffect(() => { fetchConceptos(); }, [fetchConceptos]);

    // ─── Alta / edición de concepto ───────────────────────────────────────────────
    const [editando, setEditando] = useState(false);

    const abrirModal = () => {
        setEditando(false);
        setForm({ ...FORM_EMPTY });
        setModalVisible(true);
    };

    const editarConcepto = async (row) => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${HOST_API}api/estadoFinancieroConceptos/GetConceptoById`, {
                params: { estadoFinancieroConceptoId: row.estadoFinancieroConceptoId, barrioId: barrio.id, administracionId },
            });
            setEditando(true);
            setForm({
                estadoFinancieroConceptoId: data.estadoFinancieroConceptoId,
                descripcion:                data.descripcion,
                estadoFinancieroSeccionId:  data.estadoFinancieroSeccionId,
                esAutomatico:               data.esAutomatico,
                codigoAutomatico:           data.codigoAutomatico || '',
                orden:                      data.orden,
            });
            setModalVisible(true);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar el concepto', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const guardarConcepto = async () => {
        if (!form.descripcion.trim())        { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese la descripción', life: 3000 }); return; }
        if (!form.estadoFinancieroSeccionId) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione la sección', life: 3000 }); return; }
        if (form.orden === null || form.orden === undefined) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese el orden', life: 3000 }); return; }
        if (form.esAutomatico && !form.codigoAutomatico.trim()) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese el código automático', life: 3000 }); return; }

        setSubmitting(true);
        try {
            const payload = {
                estadoFinancieroConceptoId: form.estadoFinancieroConceptoId || 0,
                barrioId:                   barrio.id,
                administracionId,
                descripcion:                form.descripcion,
                estadoFinancieroSeccionId:  form.estadoFinancieroSeccionId,
                esAutomatico:               form.esAutomatico,
                codigoAutomatico:           form.esAutomatico ? form.codigoAutomatico : null,
                orden:                      form.orden,
            };
            await axios.post(`${HOST_API}api/estadoFinancieroConceptos/AddConcepto`, payload);
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Concepto guardado correctamente', life: 3000 });
            setModalVisible(false);
            fetchConceptos();
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.errorDisplay || 'No se pudo guardar el concepto', life: 4000 });
        } finally {
            setSubmitting(false);
        }
    };

    // ─── Eliminar concepto ─────────────────────────────────────────────────────────
    const eliminarConcepto = (row) => {
        confirmDialog({
            message:      `¿Eliminar el concepto "${row.descripcion}"?`,
            header:       'Confirmar eliminación',
            icon:         'pi pi-exclamation-triangle',
            acceptLabel:  'Eliminar',
            rejectLabel:  'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.delete(`${HOST_API}api/estadoFinancieroConceptos/DeleteConcepto?estadoFinancieroConceptoId=${row.estadoFinancieroConceptoId}`);
                    toast.current.show({ severity: 'success', summary: 'Eliminado', detail: 'Concepto eliminado', life: 3000 });
                    fetchConceptos();
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar el concepto', life: 3000 });
                }
            },
        });
    };

    // ─── Columnas de la grilla ────────────────────────────────────────────────────
    const esAutomaticoBody = (row) => {
        const s = row.esAutomatico
            ? { bg: '#eff8ff', color: '#1570ef', border: '#b2ddff' }
            : { bg: '#f2f4f7', color: '#667085', border: '#d0d5dd' };
        return (
            <span style={{
                background: s.bg, color: s.color, border: `1px solid ${s.border}`,
                fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
            }}>
                {row.esAutomatico ? 'Automático' : 'Manual'}
            </span>
        );
    };

    // ─── Render ───────────────────────────────────────────────────────────────────
    return (
        <>
            <h4>Conceptos</h4>
            <br />
            <Toast ref={toast} />
            <ConfirmDialog />

            {loading && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
                </div>
            )}

            <div className="flex align-items-center gap-3 mb-3">
                <Button label="Nuevo Concepto" icon="pi pi-plus" className="p-button-sm" onClick={abrirModal} />
            </div>

            <div className="grid">
                <div className="col-12">
                    <DataTable value={conceptos} paginator rows={15} rowsPerPageOptions={[10, 15, 25, 50]}
                        showGridlines responsiveLayout="scroll"
                        emptyMessage="No hay conceptos cargados"
                        className="p-datatable-gridlines bg-white"
                        sortField="orden" sortOrder={1}>
                        <Column field="seccion"           header="Sección"            sortable style={{ width: '10rem' }} />
                        <Column field="descripcion"        header="Descripción"        sortable />
                        <Column header="Tipo" body={esAutomaticoBody} style={{ width: '9rem' }} />
                        <Column field="codigoAutomatico"   header="Código automático"  body={(r) => r.codigoAutomatico || '—'} style={{ width: '10rem' }} />
                        <Column field="orden"              header="Orden"              sortable style={{ width: '6rem', textAlign: 'center' }} />
                        <Column header="Acciones" style={{ width: '8rem', textAlign: 'center' }} body={(row) => (
                            <div className="flex gap-1 justify-content-center">
                                <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-sm"
                                    tooltip="Editar" tooltipOptions={{ position: 'top' }}
                                    onClick={() => editarConcepto(row)} />
                                <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger p-button-sm"
                                    tooltip="Eliminar" tooltipOptions={{ position: 'top' }}
                                    onClick={() => eliminarConcepto(row)} />
                            </div>
                        )} />
                    </DataTable>
                </div>
            </div>

            {/* ─── Modal: Nuevo / Editar Concepto ──────────────────────────────── */}
            <Dialog header={editando ? 'Editar Concepto' : 'Nuevo Concepto'} visible={modalVisible} style={{ width: '480px' }} modal
                onHide={() => setModalVisible(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" className="p-button-text p-button-sm" onClick={() => setModalVisible(false)} disabled={submitting} />
                        <Button label="Guardar" icon="pi pi-check" className="p-button-sm" onClick={guardarConcepto} loading={submitting} />
                    </div>
                }>
                <div className="grid p-fluid mt-1">
                    <div className="col-12">
                        <label style={lbl}>Descripción *</label>
                        <InputText value={form.descripcion} onChange={(e) => setForm(f => ({ ...f, descripcion: e.target.value }))}
                            placeholder="Descripción del concepto" className="p-inputtext-sm" />
                    </div>
                    <div className="col-12">
                        <label style={lbl}>Sección *</label>
                        <Dropdown value={form.estadoFinancieroSeccionId} options={secciones}
                            optionLabel="descripcion" optionValue="estadoFinancieroSeccionId"
                            onChange={(e) => setForm(f => ({ ...f, estadoFinancieroSeccionId: e.value }))}
                            placeholder="Seleccionar sección" className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Orden *</label>
                        <InputNumber value={form.orden} onValueChange={(e) => setForm(f => ({ ...f, orden: e.value }))}
                            className="p-inputtext-sm" />
                    </div>
                    <div className="col-6 flex align-items-center gap-2" style={{ marginTop: '1.6rem' }}>
                        <Checkbox inputId="esAutomatico" checked={form.esAutomatico}
                            onChange={(e) => setForm(f => ({ ...f, esAutomatico: e.checked, codigoAutomatico: e.checked ? f.codigoAutomatico : '' }))} />
                        <label htmlFor="esAutomatico">Es automático</label>
                    </div>
                    {form.esAutomatico && (
                        <div className="col-12">
                            <label style={lbl}>Código automático *</label>
                            <InputText value={form.codigoAutomatico} onChange={(e) => setForm(f => ({ ...f, codigoAutomatico: e.target.value }))}
                                placeholder="Código que calcula el valor automáticamente" className="p-inputtext-sm" />
                        </div>
                    )}
                </div>
            </Dialog>
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default EstadoFinancieroConceptos;
